import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import { fmtDate, fmtDateTime } from '../../../utils/ruDates';

// Дашборд отвечает на четыре вопроса: сколько пришло денег, что с
// пользователями, что с кабинетами и что сломалось. Всё, что не отвечает ни на
// один из них, здесь не живёт: ресурсы сервера — в /admin/system-health,
// подробности по задачам — в /admin/jobs.

const rub = v => `${Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽`;
const num = v => Number(v || 0).toLocaleString('ru-RU');

const Section = ({ title, hint, action, children }) => (
  <section style={{ marginBottom: 34 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
      <h2 style={{ margin: 0, fontSize: 18, color: '#1a1f36' }}>{title}</h2>
      {hint && <span style={{ fontSize: 13, color: '#8a94a6' }}>{hint}</span>}
      {action && <span style={{ marginLeft: 'auto', fontSize: 13 }}>{action}</span>}
    </div>
    {children}
  </section>
);

const Grid = ({ min = 190, children }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
    gap: 16,
  }}>{children}</div>
);

const Tile = ({ label, value, note, tone }) => (
  <div style={{
    background: 'white',
    borderRadius: 12,
    padding: '16px 18px',
    boxShadow: '0 1px 3px rgba(26,31,54,0.08)',
    borderTop: `3px solid ${tone || '#dfe3eb'}`,
  }}>
    <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: '#1a1f36', lineHeight: 1.1 }}>{value}</div>
    {note && <div style={{ fontSize: 12, color: '#8a94a6', marginTop: 6 }}>{note}</div>}
  </div>
);

const Panel = ({ children, style }) => (
  <div style={{
    background: 'white',
    borderRadius: 12,
    padding: 18,
    boxShadow: '0 1px 3px rgba(26,31,54,0.08)',
    ...style,
  }}>{children}</div>
);

const Split = ({ rows, empty }) => (
  <Panel>
    {(!rows || rows.length === 0) && <div style={{ color: '#8a94a6', fontSize: 13 }}>{empty}</div>}
    {rows?.map(r => (
      <div key={r.key} style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '7px 0',
        borderBottom: '1px solid #f3f5f8',
        fontSize: 14,
      }}>
        <span style={{ color: '#4a5568' }}>{r.key}</span>
        <strong>{r.value}</strong>
      </div>
    ))}
  </Panel>
);

// Спарклайн регистраций: столбики за две недели, без графической библиотеки.
const Spark = ({ points }) => {
  const max = Math.max(1, ...(points || []).map(p => p.count));
  return (
    <Panel>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 70 }}>
        {(points || []).map(p => (
          <div key={p.date} title={`${p.date}: ${p.count}`} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              height: `${Math.round((p.count / max) * 60)}px`,
              minHeight: p.count > 0 ? 3 : 1,
              background: p.count > 0 ? '#3498db' : '#e8ecf2',
              borderRadius: 3,
            }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#a3acb9', marginTop: 8 }}>
        <span>{points?.[0]?.date}</span>
        <span>макс. {max} за день</span>
        <span>{points?.[points.length - 1]?.date}</span>
      </div>
    </Panel>
  );
};

const JOB_TONE = { success: '#27ae60', failed: '#e74c3c', queued: '#f39c12' };
const JOB_LABEL = { success: 'успех', failed: 'ошибка', queued: 'в очереди' };

function AdminDashboard() {
  const { request } = useApi();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      setStats(await request('/api/v1/admin/stats'));
    } catch (error) {
      logger.error('Не удалось загрузить статистику дашборда', error);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { loadStats(); }, []); // eslint-disable-line

  if (loading) {
    return <div style={{ padding: 80, textAlign: 'center', color: '#697386' }}>Загрузка…</div>;
  }
  if (!stats) {
    return <div style={{ padding: 80, textAlign: 'center', color: '#e74c3c' }}>
      Статистика недоступна. Смотри логи бэкенда.
    </div>;
  }

  const { money = {}, users = {}, cabinets = {}, ops = {}, health = {} } = stats;
  const runway = money.daily_charge_total > 0
    ? Math.floor(money.balances_total / money.daily_charge_total)
    : null;

  return (
    <div className="admin-page" style={{ background: '#f8fbfc', minHeight: '100vh', padding: 30 }}>
      {stats.maintenance_mode && <div style={{
        background: '#e74c3c', color: 'white', padding: 14, borderRadius: 10,
        marginBottom: 16, fontWeight: 700, textAlign: 'center',
      }}>Система в режиме обслуживания — доступ пользователей ограничен</div>}

      {stats.system_announcement && <div style={{
        background: '#3498db', color: 'white', padding: 14, borderRadius: 10,
        marginBottom: 16, textAlign: 'center',
      }}><strong>Объявление:</strong> {stats.system_announcement}</div>}

      {(health.postgres !== 'ok' || health.redis !== 'ok') && <div style={{
        background: '#fdecea', color: '#c0392b', padding: 14, borderRadius: 10,
        marginBottom: 16, fontWeight: 600,
      }}>
        Сервисы не отвечают: {[
          health.postgres !== 'ok' && 'PostgreSQL',
          health.redis !== 'ok' && 'Redis',
        ].filter(Boolean).join(', ')}
      </div>}

      <div style={{ marginBottom: 30 }}>
        <h1 style={{ margin: 0, fontSize: 26, color: '#1a1f36' }}>Состояние платформы</h1>
        <p style={{ margin: '4px 0 0', color: '#697386', fontSize: 14 }}>
          Деньги, пользователи, кабинеты и то, что сломалось
        </p>
      </div>

      {/* ── Деньги ───────────────────────────────────────────────────────── */}
      <Section
        title="Деньги"
        hint="фактические поступления, не подписки"
        action={<Link to="/admin/billing" style={{ color: '#3498db' }}>Биллинг →</Link>}
      >
        <Grid>
          <Tile label="Пришло сегодня" value={rub(money.received_today)} tone="#27ae60" />
          <Tile label="Пришло за 7 дней" value={rub(money.received_7d)} tone="#27ae60" />
          <Tile
            label="Пришло за 30 дней"
            value={rub(money.received_30d)}
            note={money.refunded_30d > 0 ? `возвраты ${rub(money.refunded_30d)}` : 'возвратов нет'}
            tone="#27ae60"
          />
          <Tile
            label="Списывается в сутки"
            value={rub(money.daily_charge_total)}
            note={`≈ ${rub(money.daily_charge_total * 30)} в месяц`}
            tone="#2980b9"
          />
          <Tile
            label="На балансах"
            value={rub(money.balances_total)}
            note={runway !== null ? `хватит на ${runway} дн. при текущем тарифе` : 'списаний нет'}
            tone="#2980b9"
          />
          <Tile
            label="Плательщики без денег"
            value={num(money.payers_depleted)}
            note={`с деньгами ${num(money.payers_with_money)} из ${num(money.payers_total)}`}
            tone={money.payers_depleted > 0 ? '#e74c3c' : '#dfe3eb'}
          />
        </Grid>
      </Section>

      {/* ── Пользователи ─────────────────────────────────────────────────── */}
      <Section
        title="Пользователи"
        action={<Link to="/admin/users" style={{ color: '#3498db' }}>Все пользователи →</Link>}
      >
        <Grid>
          <Tile label="Всего" value={num(users.total)} tone="#3498db" />
          <Tile
            label="Новых"
            value={num(users.new_7d)}
            note={`сегодня ${num(users.new_today)} · за 30 дн. ${num(users.new_30d)}`}
            tone="#3498db"
          />
          <Tile
            label="Не подтвердили почту"
            value={num(users.email_unverified)}
            tone={users.email_unverified > 0 ? '#f39c12' : '#dfe3eb'}
          />
          <Tile
            label="Без кабинетов"
            value={num(users.without_cabinets)}
            note="зарегистрировались и не подключили магазин"
            tone={users.without_cabinets > 0 ? '#f39c12' : '#dfe3eb'}
          />
          <Tile
            label="Заблокированы"
            value={num(users.banned)}
            tone={users.banned > 0 ? '#e74c3c' : '#dfe3eb'}
          />
        </Grid>
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: '#8a94a6', marginBottom: 8 }}>Регистрации за 14 дней</div>
          <Spark points={users.registration_trend} />
        </div>
      </Section>

      {/* ── Кабинеты и тарифы ────────────────────────────────────────────── */}
      <Section
        title="Кабинеты и тарифы"
        action={<Link to="/admin/marketplace-accounts" style={{ color: '#3498db' }}>Кабинеты →</Link>}
      >
        <Grid>
          <Tile
            label="Активных кабинетов"
            value={num(cabinets.active)}
            note={`всего ${num(cabinets.total)}`}
            tone="#27ae60"
          />
          <Tile
            label="На платном тарифе"
            value={num(cabinets.paid)}
            note={cabinets.active > 0 ? `${Math.round((cabinets.paid / cabinets.active) * 100)}% активных` : null}
            tone="#27ae60"
          />
          <Tile
            label="Не синхронизировались сутки"
            value={num(cabinets.stale_sync)}
            tone={cabinets.stale_sync > 0 ? '#e74c3c' : '#dfe3eb'}
          />
          <Tile label="Проектов" value={num(cabinets.projects)} tone="#2980b9" />
          <Tile label="Плательщиков" value={num(cabinets.payers)} tone="#2980b9" />
        </Grid>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 13, color: '#8a94a6', marginBottom: 8 }}>По маркетплейсам</div>
            <Split
              rows={cabinets.by_marketplace?.map(r => ({ key: r.marketplace_type, value: num(r.count) }))}
              empty="Кабинетов нет"
            />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#8a94a6', marginBottom: 8 }}>По тарифам</div>
            <Split
              rows={cabinets.by_plan?.map(r => ({ key: r.plan_type, value: num(r.count) }))}
              empty="Активных кабинетов нет"
            />
          </div>
        </div>
      </Section>

      {/* ── Операционка ──────────────────────────────────────────────────── */}
      <Section
        title="Операционка"
        action={<Link to="/admin/jobs" style={{ color: '#3498db' }}>Задачи →</Link>}
      >
        <Grid>
          <Tile
            label="Задач упало за сутки"
            value={num(ops.jobs_failed_24h)}
            tone={ops.jobs_failed_24h > 0 ? '#e74c3c' : '#27ae60'}
          />
          <Tile
            label="Выполнено за сутки"
            value={num(ops.jobs_done_24h)}
            tone="#27ae60"
          />
          <Tile
            label="Висит в очереди"
            value={num(ops.jobs_queued)}
            tone={ops.jobs_queued > 0 ? '#f39c12' : '#dfe3eb'}
          />
          <Tile
            label="Тикетов без ответа"
            value={num(ops.unanswered_tickets)}
            note={`открытых ${num(ops.open_tickets)}`}
            tone={ops.unanswered_tickets > 0 ? '#f39c12' : '#dfe3eb'}
          />
        </Grid>

        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 16, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 13, color: '#8a94a6', marginBottom: 8 }}>Последние задачи</div>
            <Panel style={{ padding: 0 }}>
              {(!ops.recent_jobs || ops.recent_jobs.length === 0) && (
                <div style={{ padding: 18, color: '#8a94a6', fontSize: 13 }}>Задач не было</div>
              )}
              {ops.recent_jobs?.map(j => (
                <div key={j.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 18px',
                  borderBottom: '1px solid #f3f5f8',
                  fontSize: 13,
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: 4, flexShrink: 0,
                    background: JOB_TONE[j.status] || '#a3acb9',
                  }} />
                  <span style={{ fontWeight: 600 }}>{j.job_type}</span>
                  <span style={{ color: '#a3acb9' }}>{j.mode} · {j.trigger_type}</span>
                  <span style={{ marginLeft: 'auto', color: JOB_TONE[j.status] || '#697386' }}>
                    {JOB_LABEL[j.status] || j.status}
                  </span>
                  <span style={{ color: '#a3acb9', minWidth: 70, textAlign: 'right' }}>
                    {j.duration_ms !== null && j.duration_ms !== undefined
                      ? `${(j.duration_ms / 1000).toFixed(1)} с`
                      : '—'}
                  </span>
                </div>
              ))}
            </Panel>
          </div>

          <div>
            <div style={{ fontSize: 13, color: '#8a94a6', marginBottom: 8 }}>Действия админов</div>
            <Panel style={{ padding: 0 }}>
              {(!ops.recent_activity || ops.recent_activity.length === 0) && (
                <div style={{ padding: 18, color: '#8a94a6', fontSize: 13 }}>Действий не было</div>
              )}
              {ops.recent_activity?.map(a => (
                <div key={a.id} style={{
                  padding: '10px 18px',
                  borderBottom: '1px solid #f3f5f8',
                  fontSize: 13,
                }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{a.action}</span>
                    <span style={{ marginLeft: 'auto', color: '#a3acb9' }}>
                      {a.created_at ? fmtDateTime(a.created_at) : '—'}
                    </span>
                  </div>
                  <div style={{ color: '#697386' }}>
                    {a.user_email}{a.entity_name ? ` · ${a.entity_name}` : ''}
                  </div>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      </Section>

      {/* ── Контент ──────────────────────────────────────────────────────── */}
      {ops.recent_articles?.length > 0 && (
        <Section
          title="Последние статьи"
          action={<Link to="/admin/blog" style={{ color: '#3498db' }}>Блог →</Link>}
        >
          <Panel style={{ padding: 0 }}>
            {ops.recent_articles.map(a => (
              <div key={a.id} style={{
                display: 'flex',
                gap: 10,
                padding: '10px 18px',
                borderBottom: '1px solid #f3f5f8',
                fontSize: 13,
              }}>
                <span style={{ fontWeight: 600 }}>{a.title}</span>
                <span style={{ color: '#a3acb9' }}>{a.category}</span>
                <span style={{ marginLeft: 'auto', color: '#a3acb9' }}>
                  {a.published_at ? fmtDate(a.published_at) : '—'}
                </span>
              </div>
            ))}
          </Panel>
        </Section>
      )}
    </div>
  );
}

export default AdminDashboard;
